import { Button } from "@/components/ui/button";
import { RotateCcw, Edit2, Check, Undo2, Redo2, LayoutGrid, Rows3 } from "lucide-react";
import { cn } from "@/lib/utils";
import { AVAILABLE_WIDGETS, LayoutItem } from "./DashboardWidgetConfig";
import { AddWidgetDialog } from "./AddWidgetDialog";
import { ResizableWidget } from "./ResizableWidget";
import { useState, useCallback, useRef, useEffect } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Toggle } from "@/components/ui/toggle";
import { RowDirections } from "@/hooks/useDashboardLayout";

interface DraggableDashboardProps {
  layout: LayoutItem[];
  visibleWidgets: string[];
  rowDirections: RowDirections;
  isEditMode: boolean;
  canUndo: boolean;
  canRedo: boolean;
  onLayoutChange: (layout: LayoutItem[]) => void;
  onRemoveWidget: (widgetId: string) => void;
  onAddWidget: (widgetId: string) => void;
  onResetLayout: () => void;
  onToggleEditMode: () => void;
  onToggleRowDirection: (rowIndex: number) => void;
  onUndo: () => void;
  onRedo: () => void;
  renderWidget: (widgetId: string) => React.ReactNode;
}

// Height unit in pixels for the grid
const HEIGHT_UNIT = 50;
// Approximate width of a single column in pixels
const COLUMN_WIDTH_ESTIMATE = 280;

// Size presets configuration
const SIZE_PRESETS = {
  small: { w: 1, h: 2 },
  medium: { w: 1, h: 4 },
  large: { w: 2, h: 5 },
};

// First row widget IDs (stat cards)
const FIRST_ROW_WIDGETS = ["stat-users", "stat-claims", "stat-properties", "stat-status"];

export function DraggableDashboard({
  layout,
  visibleWidgets,
  rowDirections,
  isEditMode,
  canUndo,
  canRedo,
  onLayoutChange,
  onRemoveWidget,
  onAddWidget,
  onResetLayout,
  onToggleEditMode,
  onToggleRowDirection,
  onUndo,
  onRedo,
  renderWidget,
}: DraggableDashboardProps) {
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const [columnWidth, setColumnWidth] = useState(COLUMN_WIDTH_ESTIMATE);

  // Calculate actual column width based on grid container
  useEffect(() => {
    const updateColumnWidth = () => {
      if (gridRef.current) {
        const gridWidth = gridRef.current.offsetWidth;
        const gap = 16; // 1rem gap
        const columns = window.innerWidth >= 1024 ? 4 : window.innerWidth >= 768 ? 2 : 1;
        const totalGapWidth = gap * (columns - 1);
        setColumnWidth((gridWidth - totalGapWidth) / columns);
      }
    };

    updateColumnWidth();
    window.addEventListener("resize", updateColumnWidth);
    return () => window.removeEventListener("resize", updateColumnWidth);
  }, []);

  const handleDragStart = (e: React.DragEvent, widgetId: string) => {
    setDraggedId(widgetId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    if (!draggedId || draggedId === targetId) return;

    const newLayout = [...layout];
    const draggedIndex = newLayout.findIndex(item => item.i === draggedId);
    const targetIndex = newLayout.findIndex(item => item.i === targetId);

    if (draggedIndex !== -1 && targetIndex !== -1) {
      const [draggedItem] = newLayout.splice(draggedIndex, 1);
      newLayout.splice(targetIndex, 0, draggedItem);
      
      // Recalculate positions
      const updatedLayout = newLayout.map((item, index) => ({
        ...item,
        x: index % 4,
        y: Math.floor(index / 4) * item.h,
      }));
      
      onLayoutChange(updatedLayout);
    }
    setDraggedId(null);
  };

  const handleWidgetResize = useCallback((widgetId: string, width: number, height: number) => {
    // Convert pixel dimensions to grid units
    const newH = Math.max(2, Math.round(height / HEIGHT_UNIT));
    const newW = Math.max(1, Math.min(4, Math.round(width / columnWidth)));
    
    const newLayout = layout.map(item => {
      if (item.i === widgetId) {
        const widgetConfig = AVAILABLE_WIDGETS.find(w => w.id === widgetId);
        const minH = widgetConfig?.minH || 2;
        const minW = widgetConfig?.minW || 1;
        return { 
          ...item, 
          h: Math.max(minH, newH),
          w: Math.max(minW, newW),
        };
      }
      return item;
    });
    
    onLayoutChange(newLayout);
  }, [layout, onLayoutChange, columnWidth]);

  const handleSizePreset = useCallback((widgetId: string, preset: "small" | "medium" | "large") => {
    const presetConfig = SIZE_PRESETS[preset];
    
    const newLayout = layout.map(item => {
      if (item.i === widgetId) {
        const widgetConfig = AVAILABLE_WIDGETS.find(w => w.id === widgetId);
        const minH = widgetConfig?.minH || 2;
        const minW = widgetConfig?.minW || 1;
        return { 
          ...item, 
          h: Math.max(minH, presetConfig.h),
          w: Math.max(minW, presetConfig.w),
        };
      }
      return item;
    });
    
    onLayoutChange(newLayout);
  }, [layout, onLayoutChange]);

  const getWidgetHeight = (widgetId: string): number => {
    const layoutItem = layout.find(item => item.i === widgetId);
    return layoutItem ? layoutItem.h * HEIGHT_UNIT : 200;
  };

  const getWidgetWidth = (widgetId: string): number => {
    const layoutItem = layout.find(item => item.i === widgetId);
    return layoutItem ? layoutItem.w * columnWidth : columnWidth;
  };

  const getColumnSpan = (widgetId: string): number => {
    const layoutItem = layout.find(item => item.i === widgetId);
    return layoutItem?.w || 1;
  };

  const sortedWidgets = visibleWidgets
    .map(id => layout.find(l => l.i === id))
    .filter(Boolean) as LayoutItem[];

  // Separate first row widgets from other widgets
  const firstRowWidgets = sortedWidgets.filter(item => FIRST_ROW_WIDGETS.includes(item.i));
  const otherWidgets = sortedWidgets.filter(item => !FIRST_ROW_WIDGETS.includes(item.i));

  // Group other widgets into rows of 4
  const widgetRows: LayoutItem[][] = [];
  for (let i = 0; i < otherWidgets.length; i += 4) {
    widgetRows.push(otherWidgets.slice(i, i + 4));
  }

  const renderWidgetItem = (item: LayoutItem) => {
    const colSpan = getColumnSpan(item.i);
    return (
      <div
        key={item.i}
        draggable={isEditMode}
        onDragStart={(e) => handleDragStart(e, item.i)}
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, item.i)}
        className={cn(
          "transition-all",
          isEditMode && "cursor-move",
          draggedId === item.i && "opacity-50",
          // Dynamic column span based on width
          colSpan === 2 && "md:col-span-2",
          colSpan === 3 && "md:col-span-2 lg:col-span-3",
          colSpan === 4 && "md:col-span-2 lg:col-span-4",
        )}
      >
        <ResizableWidget
          widgetId={item.i}
          isEditMode={isEditMode}
          onRemove={() => onRemoveWidget(item.i)}
          onResize={handleWidgetResize}
          onSizePreset={handleSizePreset}
          initialHeight={getWidgetHeight(item.i)}
          initialWidth={getWidgetWidth(item.i)}
        >
          {renderWidget(item.i)}
        </ResizableWidget>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end gap-2 flex-wrap">
        <TooltipProvider>
          <div className="flex items-center gap-1 mr-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="outline" 
                  size="icon" 
                  onClick={onUndo}
                  disabled={!canUndo}
                  className="h-8 w-8"
                >
                  <Undo2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Undo (Ctrl+Z)</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="outline" 
                  size="icon" 
                  onClick={onRedo}
                  disabled={!canRedo}
                  className="h-8 w-8"
                >
                  <Redo2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Redo (Ctrl+Y)</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
        <AddWidgetDialog
          visibleWidgets={visibleWidgets}
          onAddWidget={onAddWidget}
          onRemoveWidget={onRemoveWidget}
        />
        <Button variant="outline" size="sm" onClick={onResetLayout}>
          <RotateCcw className="h-4 w-4 mr-2" />
          Reset Layout
        </Button>
        <Button 
          variant={isEditMode ? "default" : "outline"}
          size="sm"
          onClick={onToggleEditMode}
        >
          {isEditMode ? (
            <><Check className="h-4 w-4 mr-2" />Done Editing</>
          ) : (
            <><Edit2 className="h-4 w-4 mr-2" />Edit Layout</>
          )}
        </Button>
      </div>

      {isEditMode && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 rounded-lg p-3">
          <span className="font-medium">Tip:</span>
          <span>Drag corners/edges to resize, use preset buttons (S/M/L) for quick sizing, or drag the grip handle to reorder. Click the row toggle to switch between horizontal and vertical layout.</span>
        </div>
      )}

      {/* First row - always horizontal grid */}
      {firstRowWidgets.length > 0 && (
        <div 
          ref={gridRef}
          className="grid gap-4 md:grid-cols-2 lg:grid-cols-4"
        >
          {firstRowWidgets.map(renderWidgetItem)}
        </div>
      )}

      {/* Other rows - toggleable direction */}
      {widgetRows.map((row, rowIndex) => {
        const direction = rowDirections[rowIndex] || "vertical";
        const isVertical = direction === "vertical";
        
        return (
          <div key={rowIndex} className="space-y-2">
            {/* Row header with direction toggle */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-muted-foreground">
                Row {rowIndex + 2}
              </span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Toggle
                      variant="outline"
                      size="sm"
                      pressed={isVertical}
                      onPressedChange={() => onToggleRowDirection(rowIndex)}
                      className="h-8 gap-2"
                    >
                      {isVertical ? (
                        <>
                          <Rows3 className="h-4 w-4" />
                          <span className="text-xs">Vertical</span>
                        </>
                      ) : (
                        <>
                          <LayoutGrid className="h-4 w-4" />
                          <span className="text-xs">Horizontal</span>
                        </>
                      )}
                    </Toggle>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Toggle between horizontal and vertical layout</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            
            {/* Widgets in this row */}
            <div 
              className={cn(
                "gap-4",
                isVertical 
                  ? "flex flex-col" 
                  : "grid md:grid-cols-2 lg:grid-cols-4"
              )}
            >
              {row.map(renderWidgetItem)}
            </div>
          </div>
        );
      })}
    </div>
  );
}
